import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (!authService.isAuthenticated()) {
        setLoading(false);
        return;
      }

      // Use cached user immediately, then refresh roles/user data from server.
      const cachedUser = authService.getUser();
      if (cachedUser) {
        setUser(cachedUser);
      }

      try {
        const me = await authService.getMe();
        if (me?.success && me?.data) {
          setUser(me.data);
          localStorage.setItem('user', JSON.stringify(me.data));
        }
      } catch (error) {
        // Keep cached user if /me fails.
        console.error('Failed to refresh auth user:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const data = await authService.login(email, password);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const hasRole = (role) => {
    const normalizedTarget = String(role || '').trim().toLowerCase();
    const userRoles = Array.isArray(user?.roles) ? user.roles : [];
    return userRoles.some((userRole) => String(userRole || '').trim().toLowerCase() === normalizedTarget);
  };

  const value = {
    user,
    login,
    logout,
    hasRole,
    isAuthenticated: !!user,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
